import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar, GridRenderCellParams } from "@mui/x-data-grid";
import { getCoachesList, deleteCoach } from "../../API/coach-mgmt";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddNewCoach from "../../components/addNewCoach/addCoach";
import MainTable from "../../components/Datagrid/dataGridTable";
import { SUCCESS_TOAST, ERROR_TOAST } from "../../helpers/toasters";
import { useSelector } from "react-redux";

export default function CoachList(props) {
  const defaultCreateCoachState = {
    name: "",
    email: "",
    password: "",
    isSuper: false,
    category: "",
    active: false,
    trainingModel: "",
    dob: "",
    contact: 0,
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: 0,
      country: "",
    },
    city: "",
  };

  const [coaches, setCoaches] = useState<any[]>([]);
  const [rows, setRows] = useState<any>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [searchRows, setSearchRows] = useState<any[]>([]);
  const [updateCoach, setUpdateCoach] = React.useState<boolean>(false);
  const [updatingRow, setUpdatingRow] = React.useState<any>(
    defaultCreateCoachState
  );
  const user = useSelector((state: any) => state.auth);

  useEffect(() => {
    getFirstData();
  }, []);

  useEffect(() => {
    if (!props.search?.trim()) {
      setSearchRows(rows);
    } else {
      const filteredRows = rows.filter((row: any) =>
        (row.name ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.email ?? "").toString().toLowerCase().includes(props.search.toLowerCase()) ||
        ((row.course?.[0]?.name ?? "").toLowerCase().includes(props.search.toLowerCase())) ||
        (row.contact ?? "").toString().toLowerCase().includes(props.search.toLowerCase()) ||
        (row.city ?? "").toString().toLowerCase().includes(props.search.toLowerCase()) ||
        (row.country ?? "").toString().toLowerCase().includes(props.search.toLowerCase())
      );
      setSearchRows(filteredRows);
    }
  }, [props.search, rows]);

  const handleDelete = async (row: any) => {
    if (window.confirm("Are you sure you want to delete this Coach?")) {
      try {
        await deleteCoach(row._id);
        
        SUCCESS_TOAST("Coach deleted successfully! ✅");
        getFirstData();
      } catch (error) {
        console.error("Failed to delete Coach:", error);
        ERROR_TOAST("Failed to delete Coach ❌");
      }
    }
  };

  const getFirstData = async () => {
    try {
      const data = await getCoachesList();
      // Transform data to ensure export-friendly values
      const transformedData = data.map((coach) => ({
        ...coach,
        batches: Array.isArray(coach.batches)
          ? coach.batches.map((batch) => batch.batch_id).join(", ") || "No Courses"
          : "No Courses",
        address: coach.address
          ? typeof coach.address === "string"
            ? coach.address
            : `${coach.address.street || ""}, ${coach.address.city || ""}, ${coach.address.state || ""
            }, ${coach.address.postalCode || ""}, ${coach.address.country || ""}`
          : "",
        contact: coach.contact ? String(coach.contact) : "",
        trainingModel: coach.trainingModel || "",
      }));
      setCoaches(transformedData);
      setRows(transformedData);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch coaches:", error);
      ERROR_TOAST("Failed to fetch coaches ❌");
    }
  };

  // Base columns visible to all users (including Edit)
  const baseColumns = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "name",
      headerName: "Coach / Mentor",
      width: 140,
      editable: true,
    },
    {
      field: "batches",
      headerName: "Batch IDs",
      width: 210,
      editable: false,
      renderCell: (params) => {
        return <span>{params.value || "No Courses"}</span>;
      },
      valueGetter: (params) => params.value || "No Courses",
    },
    {
      field: "trainingModel",
      headerName: "Training Mode",
      width: 130,
      editable: false,
      valueGetter: (params) => params.value || "",
    },
    {
      field: "contact",
      headerName: "Mobile Number",
      width: 120,
      editable: false,
      valueGetter: (params) => params.value || "",
    },
    {
      field: "address",
      headerName: "Address",
      width: 260,
      editable: false,
      valueGetter: (params) => params.value || "",
    },
    {
      field: "city",
      headerName: "City",
      width: 100,
      editable: false,
    },
    {
      field: "country",
      headerName: "Country",
      width: 100,
      editable: false,
    },
    {
      field: "Edit",
      width: 60,
      renderCell: (cellValues) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <EditRoundedIcon
            style={{ color: "#8E8EA1", cursor: "pointer" }}
            onClick={(event) => handleCellClick(event, cellValues)}
          />
        </div>
      ),
    },
  ];

  // Delete column visible only to superusers
  const deleteColumn = [
    {
      field: "Delete",
      width: 60,
      renderCell: (cellValues) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DeleteRoundedIcon
            style={{ color: "#8E8EA1", cursor: "pointer" }}
            onClick={() => handleDelete(cellValues.row)}
          />
        </div>
      ),
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper ? [...baseColumns, ...deleteColumn] : baseColumns;

  const handleCellClick = (e: React.MouseEvent, value: GridRenderCellParams) => {
    const { row } = value;
    
    setUpdateCoach(true);
    setUpdatingRow(row);
  };

  const handelClose = () => {
    setUpdateCoach(false);
    getFirstData();
  };

  return (
    <>
      {searchRows.length === 0 && props.search?.trim() ? (
        <p className="no-results">🚫 No results found</p>
      ) : (
        <MainTable
          rows={searchRows}
          columns={columns}
          loading={loading}
        />
      )}

      {updateCoach && (
        <AddNewCoach handelClose={handelClose} data={updatingRow} />
      )}
    </>
  );
}